import React, { useEffect, useRef, useState } from 'react';
import {
  Bold, Italic, Underline, Link as LinkIcon, Code2, Eye, RotateCcw, Type, List, ListOrdered,
} from 'lucide-react';

/**
 * Lightweight rich-text editor for email bodies (templates + ad-hoc compose).
 * Same look-and-feel as SignatureEditor but without the preset / logo concerns.
 *
 * Props:
 *   value         (string)   current HTML
 *   onChange      (fn)       called on every edit with new HTML
 *   placeholder   (string)   placeholder shown when empty (Visual mode only)
 *   minHeight     (number)   editor min-height in px (default 200)
 */
const RichBodyEditor = ({ value = '', onChange, placeholder = '', minHeight = 200 }) => {
  const editorRef = useRef(null);
  const [mode, setMode] = useState('visual'); // 'visual' | 'html'
  const [showPreview, setShowPreview] = useState(false);

  // Keep contentEditable HTML in sync when value or mode changes,
  // but never while the user is typing (would clobber caret).
  useEffect(() => {
    if (mode === 'visual' && editorRef.current && editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = value || '';
    }
  }, [value, mode]);

  const exec = (cmd, arg = null) => {
    document.execCommand(cmd, false, arg);
    if (editorRef.current) onChange?.(editorRef.current.innerHTML);
  };

  const insertLink = () => {
    const url = window.prompt('Enter URL (e.g. https://paify.io)');
    if (!url) return;
    exec('createLink', url);
  };

  const handleVisualInput = () => {
    if (editorRef.current) onChange?.(editorRef.current.innerHTML);
  };

  const ToolbarBtn = ({ onClick, title, children, testid }) => (
    <button
      type="button"
      onClick={onClick}
      title={title}
      data-testid={testid}
      className="px-2 py-1.5 text-gray-300 hover:text-white hover:bg-gray-700 rounded transition-colors"
    >
      {children}
    </button>
  );

  return (
    <div className="border border-gray-700 rounded-lg overflow-hidden bg-gray-900" data-testid="rich-body-editor">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-1 px-2 py-1.5 bg-gray-800 border-b border-gray-700">
        {mode === 'visual' && (
          <>
            <ToolbarBtn onClick={() => exec('bold')} title="Bold" testid="body-bold"><Bold className="w-4 h-4" /></ToolbarBtn>
            <ToolbarBtn onClick={() => exec('italic')} title="Italic" testid="body-italic"><Italic className="w-4 h-4" /></ToolbarBtn>
            <ToolbarBtn onClick={() => exec('underline')} title="Underline" testid="body-underline"><Underline className="w-4 h-4" /></ToolbarBtn>
            <span className="w-px h-5 bg-gray-700 mx-1" />
            <ToolbarBtn onClick={insertLink} title="Insert link" testid="body-link"><LinkIcon className="w-4 h-4" /></ToolbarBtn>
            <span className="w-px h-5 bg-gray-700 mx-1" />
            <ToolbarBtn onClick={() => exec('insertUnorderedList')} title="Bullet list" testid="body-ul"><List className="w-4 h-4" /></ToolbarBtn>
            <ToolbarBtn onClick={() => exec('insertOrderedList')} title="Numbered list" testid="body-ol"><ListOrdered className="w-4 h-4" /></ToolbarBtn>
            <span className="w-px h-5 bg-gray-700 mx-1" />
            <ToolbarBtn onClick={() => exec('formatBlock', 'p')} title="Paragraph" testid="body-para"><Type className="w-4 h-4" /></ToolbarBtn>
            <ToolbarBtn onClick={() => exec('removeFormat')} title="Clear formatting" testid="body-clear"><RotateCcw className="w-4 h-4" /></ToolbarBtn>
          </>
        )}
        <div className="ml-auto flex items-center gap-1">
          <button
            type="button"
            onClick={() => setShowPreview((v) => !v)}
            data-testid="body-toggle-preview"
            className="px-2 py-1.5 text-xs text-gray-300 hover:text-white hover:bg-gray-700 rounded inline-flex items-center gap-1"
            title="Toggle preview"
          >
            <Eye className="w-3.5 h-3.5" />{showPreview ? 'Hide preview' : 'Preview'}
          </button>
          <button
            type="button"
            onClick={() => setMode((m) => (m === 'visual' ? 'html' : 'visual'))}
            data-testid="body-toggle-mode"
            className="px-2 py-1.5 text-xs text-gray-300 hover:text-white hover:bg-gray-700 rounded inline-flex items-center gap-1"
            title="Switch between visual and HTML"
          >
            <Code2 className="w-3.5 h-3.5" />{mode === 'visual' ? 'HTML' : 'Visual'}
          </button>
        </div>
      </div>

      {/* Editor surface */}
      {mode === 'visual' ? (
        <div
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning
          onInput={handleVisualInput}
          onBlur={handleVisualInput}
          data-testid="body-visual-area"
          data-placeholder={placeholder}
          className="p-3 bg-white text-gray-900 focus:outline-none [&_a]:text-blue-600 [&_a]:underline [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:my-2 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:my-2 [&_li]:my-1 [&[data-placeholder]:empty:before]:content-[attr(data-placeholder)] [&[data-placeholder]:empty:before]:text-gray-400"
          style={{ minHeight, fontFamily: 'Segoe UI, Helvetica, Arial, sans-serif', fontSize: 14, lineHeight: 1.55 }}
        />
      ) : (
        <textarea
          value={value || ''}
          onChange={(e) => onChange?.(e.target.value)}
          spellCheck={false}
          data-testid="body-html-area"
          className="w-full p-3 bg-gray-900 text-gray-100 font-mono text-xs focus:outline-none border-0 resize-y"
          style={{ minHeight }}
          placeholder={placeholder || '<p>Hi {{first_name}},</p>\n<p>...</p>'}
        />
      )}

      {/* Preview (read-only render of current value) */}
      {showPreview && (
        <div className="border-t border-gray-700 bg-white" data-testid="body-preview">
          <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 text-[11px] uppercase tracking-wide text-gray-500">
            Live preview
          </div>
          <div
            className="p-4 text-gray-800 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:my-2 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:my-2 [&_li]:my-1"
            style={{ fontFamily: 'Segoe UI, Helvetica, Arial, sans-serif', fontSize: 14, lineHeight: 1.55 }}
            dangerouslySetInnerHTML={{ __html: value || '<em style="color:#999">Empty body.</em>' }}
          />
        </div>
      )}
    </div>
  );
};

export default RichBodyEditor;
